import React from 'react';
import styled from 'styled-components';
import {
  ClientModel,
  CurrentClientManagerContextData,
  EntityModel,
  SegmentModel,
  SelectedMerchant,
} from '@cko/dashboard-shared';
import { Grid } from '@cko/primitives';

import { AccountSelectorList, InfiniteScroll, ListItem } from '../AccountSelectorList';

interface AccountSelectorNewViewSelectorProps {
  existingView: Partial<CurrentClientManagerContextData>;
  newView: SelectedMerchant | undefined;
  setNewView: (view: SelectedMerchant) => void;
  clients: Array<ClientModel>;
  fetchMoreClients: () => void;
  hasMoreClients?: boolean;
  isFetchingMoreClients?: boolean;
}

const NewViewSelectorContainer = styled(Grid)`
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  width: 100%;
  height: 430px;
  overflow-y: hidden;
  overflow-x: auto;
`;

const clientToListItemMapper = (client: ClientModel): ListItem => {
  return {
    title: client.name,
    nestedCount: client.entities ? client.entities.length : undefined,
    isDisabled: !client.entities || client.entities.length === 0,
  };
};

const segmentToListItemMapper = (segment: SegmentModel): ListItem => {
  return {
    title: segment.name,
    isInactive: !segment.isActive,
  };
};

export const AccountSelectorNewViewSelector = ({
  newView,
  setNewView,
  existingView,
  clients,
  hasMoreClients,
  isFetchingMoreClients,
  fetchMoreClients,
}: AccountSelectorNewViewSelectorProps) => {
  const clientInfiniteScroll: InfiniteScroll = {
    hasMore: hasMoreClients,
    isFetchingMore: isFetchingMoreClients,
    fetchMore: fetchMoreClients,
  };

  const toggleSelectedClient = (client: ClientModel) => {
    setNewView({
      client,
      entity: undefined,
      segment: undefined,
    });
  };

  const toggleSelectedEntity = (entity: EntityModel) => {
    const updatedView: SelectedMerchant = {
      client: newView?.client,
      segment: undefined,
    };

    if (newView?.segment) {
      updatedView.entity = entity;
    } else {
      updatedView.entity = entity.id === newView?.entity?.id ? undefined : entity;
    }

    setNewView(updatedView);
  };

  const toggleSelectedSegment = (segment: SegmentModel) => {
    setNewView({
      client: newView?.client,
      entity: newView?.entity,
      segment: segment.id === newView?.segment?.id ? undefined : segment,
    });
  };

  const entityToListItemMapper = (entity: EntityModel): ListItem => {
    return {
      title: entity.name,
      nestedCount: entity.entitySegments.length > 0 ? entity.entitySegments.length : undefined,
    };
  };

  return (
    <NewViewSelectorContainer>
      <AccountSelectorList<ClientModel>
        listName="Client"
        items={clients}
        selectedItem={newView?.client}
        existingSelectedItem={existingView?.client}
        itemToListItemMapper={clientToListItemMapper}
        onToggleItem={toggleSelectedClient}
        infiniteScroll={hasMoreClients ? clientInfiniteScroll : undefined}
        disabledMessage="You cannot select your account as it does not have any legal entities setup yet."
        isParent={!!(newView?.entity || newView?.segment)}
      />
      {newView?.client && newView?.client?.entities && newView?.client?.entities?.length > 0 && (
        <AccountSelectorList<EntityModel>
          listName="Entity"
          items={newView?.client.entities}
          itemToListItemMapper={entityToListItemMapper}
          onToggleItem={toggleSelectedEntity}
          selectedItem={newView.entity}
          existingSelectedItem={existingView?.entity}
          isParent={!!newView?.segment}
        />
      )}
      {newView?.entity && newView.entity.entitySegments.length > 0 && (
        <AccountSelectorList<SegmentModel>
          listName="Entity segment"
          items={newView.entity.entitySegments}
          selectedItem={newView?.segment}
          itemToListItemMapper={segmentToListItemMapper}
          onToggleItem={toggleSelectedSegment}
          existingSelectedItem={existingView?.segment}
          isParent={false}
        />
      )}
    </NewViewSelectorContainer>
  );
};
